import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  GUILD_WAR_PATH_ROLE_COLORS,
  GUILD_WAR_PATH_REGISTRY,
  type GuildWarPathEntry,
  type GuildWarPathRoleTag,
  type MartialPathKey,
} from "@/core/guild-war/path-registry"
import { cn } from "@/lib/utils"

type PathOption = {
  value: MartialPathKey | "none"
  entry: GuildWarPathEntry | null
  description: string | null
  roleTag: GuildWarPathRoleTag | null
}

const PATH_OPTIONS: PathOption[] = [
  {
    value: "none",
    entry: null,
    description: null,
    roleTag: null,
  },
  ...GUILD_WAR_PATH_REGISTRY.map((entry) => ({
    value: entry.key,
    entry,
    description: entry.description,
    roleTag: entry.roleTag,
  })),
]

function PathOptionContent({
  option,
  showDescription = false,
}: {
  option: PathOption
  showDescription?: boolean
}) {
  if (option.value === "none") {
    return <span className="text-muted-foreground">Not set</span>
  }

  const { entry, description, roleTag } = option

  if (!entry) return null

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span className="text-lg" aria-hidden="true">
          {entry.emoji}
        </span>
        <span className="font-medium">{entry.fullLabel}</span>
        {roleTag && (
          <span
            className={cn(
              "rounded-md px-1.5 py-0.5 text-xs font-medium",
              GUILD_WAR_PATH_ROLE_COLORS[roleTag],
            )}
          >
            {roleTag}
          </span>
        )}
      </div>
      {showDescription && description && (
        <span className="ml-7 text-xs text-muted-foreground">
          {description}
        </span>
      )}
    </div>
  )
}

function PathTriggerContent({ option }: { option: PathOption }) {
  if (option.value === "none") {
    return <span className="text-muted-foreground">Not set</span>
  }

  const { entry, roleTag } = option

  if (!entry) return null

  return (
    <div className="flex items-center gap-2">
      <span className="text-base" aria-hidden="true">
        {entry.emoji}
      </span>
      <span>{entry.shortLabel}</span>
      {roleTag && (
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-xs font-medium",
            GUILD_WAR_PATH_ROLE_COLORS[roleTag],
          )}
        >
          {roleTag}
        </span>
      )}
    </div>
  )
}

type PathSelectorProps = {
  value: MartialPathKey | "none"
  onValueChange: (value: MartialPathKey | "none") => void
  excludeValues?: (MartialPathKey | "none")[]
  label?: string
  hint?: string
  error?: string
  disabled?: boolean
  className?: string
  required?: boolean
}

export function PathSelector({
  value,
  onValueChange,
  excludeValues = [],
  label,
  hint,
  error,
  disabled,
  className,
  required,
}: PathSelectorProps) {
  const filteredOptions = PATH_OPTIONS.filter(
    (opt) => !excludeValues.includes(opt.value),
  )

  const selectedOption = PATH_OPTIONS.find((opt) => opt.value === value)

  return (
    <div className={cn("grid gap-1.5", className)}>
      {label && (
        <Label
          className={cn("flex items-center gap-1", error && "text-destructive")}
        >
          {label}
          {required && (
            <span className="text-destructive" aria-hidden="true">
              *
            </span>
          )}
        </Label>
      )}
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => onValueChange(next as MartialPathKey | "none")}
      >
        <SelectTrigger
          className={cn(
            "w-full",
            error && "border-destructive focus:ring-destructive",
          )}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${label}-error` : hint ? `${label}-hint` : undefined
          }
        >
          <SelectValue>
            {selectedOption ? (
              <PathTriggerContent option={selectedOption} />
            ) : (
              "Select a path"
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="start" className="min-w-80">
          {filteredOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="py-2"
            >
              <PathOptionContent option={option} showDescription />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && !error && (
        <p id={`${label}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${label}-error`}
          className="text-xs text-destructive"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  )
}

export { GUILD_WAR_PATH_ROLE_COLORS }
